export type InvoiceStatus =
  | "draft"
  | "sent"
  | "partially_paid"
  | "paid"
  | "overdue"
  | "void";

export type InvoiceLanguage = "zh-HK" | "en";
export type PaperSize = "a4" | "letter";

export interface PartySnapshot {
  name: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  registrationNumber: string;
}

export interface BillingProfile {
  id: string;
  ownerUserId: string;
  displayName: string;
  logoPath: string;
  address: string;
  email: string;
  phone: string;
  website: string;
  registrationNumber: string;
  invoicePrefix: string;
  defaultPaymentDays: number;
  brandColour: string;
  paymentInstructions: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Client {
  id: string;
  ownerUserId: string;
  billingProfileId: string;
  party: PartySnapshot;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  rateMinor: number;
  amountMinor: number;
}

export type Adjustment =
  | {
      mode: "multiplier";
      multiplier: number;
    }
  | {
      mode: "fixed";
      amountMinor: number;
    };

export interface PaymentRecord {
  id: string;
  amountMinor: number;
  paymentDate: string;
  method: string;
  reference: string;
  note: string;
}

export type ReceiptMimeType = "image/jpeg" | "image/png" | "image/webp";

export interface ReceiptAttachment {
  id: string;
  ownerUserId: string;
  billingProfileId: string;
  invoiceId: string;
  storagePath: string;
  fileName: string;
  mimeType: ReceiptMimeType;
  fileSize: number;
  position: number;
  createdAt: string;
}

export interface Invoice {
  id: string;
  ownerUserId: string;
  billingProfileId: string;
  number: string;
  status: InvoiceStatus;
  issueDate: string;
  dueDate: string;
  purchaseOrderNumber: string;
  projectReference: string;
  currency: "HKD";
  language: InvoiceLanguage;
  paperSize: PaperSize;
  issuer: PartySnapshot;
  client: PartySnapshot;
  items: LineItem[];
  discount: Adjustment | null;
  subtotalMinor: number;
  discountMinor: number;
  totalMinor: number;
  clientNotes: string;
  internalNotes: string;
  paymentInstructions: string;
  terms: string;
  authorisedName: string;
  payments: PaymentRecord[];
  receipts: ReceiptAttachment[];
  paidMinor: number;
  balanceMinor: number;
  createdAt: string;
  updatedAt: string;
}

export type DrawerName =
  | "records"
  | "clients"
  | "profiles"
  | "export"
  | "account"
  | "payments"
  | "history"
  | null;

export interface TextSuggestion {
  kind: "description" | "unit" | "client_notes" | "internal_notes";
  value: string;
  useCount: number;
  lastUsedAt: string;
}

export interface PersistedWorkspace {
  version: 1;
  profiles: BillingProfile[];
  clients: Client[];
  invoices: Invoice[];
  activeProfileId: string;
  currentInvoiceId: string;
}

export const emptyParty = (): PartySnapshot => ({
  name: "",
  contactName: "",
  email: "",
  phone: "",
  address: "",
  registrationNumber: "",
});
